import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { aiGradientTextSx } from 'common/constants/ai';
import { RowFormat } from 'common/types/scalarSpreadsheet';
import { DEFAULT_CURRENCY } from 'components/FeaturedSpreadsheet/constants';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { useDocumentFootnotes } from 'context/DocumentFootnotesContext';
import CitationService from 'services/citations';
import {
  useCellShowsMerge,
  useReviewDrawerOpen,
  useReviewerMode,
  useReviewItemBusy,
  useReviewItemStatus,
  useReviewStore,
} from 'store/useReviewStore';
import { AIMergeValueViewerProps } from './AIMergeValueViewer.props';
import {
  getMergeBoxSx,
  mergeAIValueSx,
  mergeArrowSx,
  mergeCurrentValueSx,
  mergeTypographySx,
  restPurpleAnchorSx,
  restRevealAnchorSx,
} from './AIMergeValueViewer.styles';
import AISecurityFindingPopover from './AISecurityFindingPopover';
import { isFindingPopoverClick } from './findingPopoverClick';
import FastValueViewer from '../../../../../components/ScalarSpreadsheet/utilities/FastValueViewer';
import { finiteNumberOf } from '../../utilities/aiNumericValue';
import { formatDisplayValue } from '../../utilities/formatAiDisplayValue';
import { matchAiOption, resolveAiOptionValue } from '../../utilities/resolveAiOptionValue';
import { reviewItemId, reviewStatusOf } from '../../utilities/reviewAggregator';
import { useAIFieldAcceptContext } from '../captable-table/AIFieldAcceptContext';

const FallbackValueViewer = ({
  value,
  originalValueViewer: OriginalViewer,
  restProps,
  onDoubleClick,
}: {
  value: string | number;
  originalValueViewer?: React.ComponentType<any>;
  restProps: Record<string, any>;
  onDoubleClick: (event: React.MouseEvent) => void;
}) => {
  if (OriginalViewer) {
    return <OriginalViewer value={value} {...restProps} onDoubleClick={onDoubleClick} />;
  }
  return <FastValueViewer value={value} {...restProps} onDoubleClick={onDoubleClick} />;
};

const AIMergeValueViewer = ({
  value,
  aiFieldMetadata,
  aiAccuracyConfidence,
  fieldLabel,
  securityId,
  fieldAlias,
  onRemoveFinding,
  originalValueViewer: OriginalValueViewer,
  ...restProps
}: AIMergeValueViewerProps) => {
  const [ctrlHeld, setCtrlHeld] = useState(false);
  const { acceptField, dismissField } = useAIFieldAcceptContext();
  const { format: spreadsheetFormat } = useContext(FeaturedSpreadsheetContext);
  const currencyCode = (spreadsheetFormat as any)?.currency?.code ?? DEFAULT_CURRENCY;
  const [, setLocalVersion] = useState(0);

  useEffect(() => {
    // Both key events report the modifiers still held, so one listener tracks the affordance.
    const onKey = (e: KeyboardEvent) => setCtrlHeld(isFindingPopoverClick(e));
    const onBlur = () => setCtrlHeld(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
      window.removeEventListener('blur', onBlur);
    };
  }, []);
  const { getDocumentMetadata } = useDocumentFootnotes();
  const citationService = useMemo(() => new CitationService(), []);

  // Review status: the store item (flipped live by the review panel) is
  // authoritative, because a settle mutates the cell's metadata WITHOUT changing
  // its value - and withCellEventListener's repaint bails out on an unchanged
  // value, so this viewer would keep rendering stale metadata. Subscribing to the
  // store both re-renders this cell the moment its card settles AND keeps the
  // grid, the panel, and the popover in agreement. Metadata is the fallback when
  // no item exists (drawer closed / items not aggregated).
  const storeStatus = useReviewItemStatus(securityId, fieldAlias);
  const status = storeStatus ?? reviewStatusOf(aiFieldMetadata);
  // A still-pending field PREVIEWS the AI value at rest; once settled
  // (accepted/dismissed) the cell falls back to its own value viewer.
  const isPending = status === 'pending';
  // The cell offers its own decision only while none is still saving on its card.
  const busy = useReviewItemBusy(securityId, fieldAlias);
  const canDecide = isPending && !busy;

  // Settle parity with the review panel: once the API call resolves, flip the
  // store item so the side-panel card moves to Reviewed instantly and (via
  // setReviewItemStatus) any stale hovered/selected ref on this cell clears.
  // The card stays busy until the call answers, so no other decision starts on it meanwhile.
  const handleAccept = useCallback(() => {
    if (securityId && fieldAlias) {
      const id = reviewItemId(securityId, fieldAlias);
      const { holdCards, setReviewItemStatus } = useReviewStore.getState();
      const release = holdCards([id]);
      acceptField(securityId, fieldAlias)
        .then(() => setReviewItemStatus(id, 'accepted'))
        .finally(release);
    }
  }, [securityId, fieldAlias, acceptField]);

  const handleDismiss = useCallback(() => {
    if (securityId && fieldAlias) {
      const id = reviewItemId(securityId, fieldAlias);
      const { holdCards, setReviewItemStatus } = useReviewStore.getState();
      const release = holdCards([id]);
      dismissField(securityId, fieldAlias)
        .then(() => setReviewItemStatus(id, 'dismissed'))
        .finally(release);
    }
  }, [securityId, fieldAlias, dismissField]);

  const cellOnDoubleClick = restProps.onDoubleClick as ((event: React.MouseEvent) => void) | undefined;
  const wrappedOnDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      if (isFindingPopoverClick(event)) {
        return;
      }
      cellOnDoubleClick?.(event);
    },
    [cellOnDoubleClick]
  );

  const pendingCitation = useMemo(
    () => restProps.cell?.citationReferences?.find((c: any) => c.is_accepted === false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [restProps.cell?.citationReferences]
  );

  const pendingCitationDocumentName = useMemo(() => {
    if (!pendingCitation) {
      return undefined;
    }
    return getDocumentMetadata(pendingCitation.document_reference)?.documentName;
  }, [pendingCitation, getDocumentMetadata]);

  const handleAcceptCitation = useCallback(() => {
    if (!pendingCitation?.id || !restProps.cell?.citationReferences) {
      return;
    }

    const citationId = pendingCitation.id;

    // Update local cell state immediately for instant UI feedback
    // eslint-disable-next-line no-param-reassign
    restProps.cell.citationReferences = restProps.cell.citationReferences.map((c: any) =>
      c.id === citationId ? { ...c, is_accepted: true } : c
    );
    setLocalVersion(v => v + 1);

    citationService.updateCitationAcceptance(citationId, true).catch(() => {
      // Revert on failure
      // eslint-disable-next-line no-param-reassign
      restProps.cell.citationReferences = restProps.cell.citationReferences?.map((c: any) =>
        c.id === citationId ? { ...c, is_accepted: false } : c
      );
      setLocalVersion(v => v + 1);
    });
  }, [pendingCitation, restProps.cell, citationService]);

  const aiValue = aiFieldMetadata.value;
  const cellValue = restProps.cell?.value;
  // "Previous" side of the merge: prefer the durable review-trail baseline the
  // backend stamps on the entry (previous_value: the pre-apply value, carried
  // forward across re-applies while pending; null = the apply created the
  // security, so no pre-AI value). The LIVE cell value lies after successive
  // applies - an applied-but-unreviewed AI value would masquerade as the
  // baseline. Legacy metadata without the key falls back to the live value.
  const baselineValue = 'previous_value' in aiFieldMetadata ? aiFieldMetadata.previous_value : (cellValue ?? value);
  const dbType = restProps.cell?.dbType as string | undefined;
  const cellFormat = restProps.cell?.format as RowFormat | undefined;
  const gridType = restProps.cell?.gridType as string | undefined;
  // Select-backed cells (cell.options - e.g. Underlying Security) store option
  // IDs (sibling security_ref UUIDs) while the AI suggests the option NAME:
  // resolve BOTH sides through the options so the merge text reads
  // "name -> name", never a raw UUID.
  const cellOptions = restProps.cell?.options as { id?: unknown; name?: unknown }[] | undefined;
  const currentDisplay = useMemo(
    () =>
      matchAiOption(cellOptions, baselineValue)?.label ??
      formatDisplayValue(baselineValue, dbType, fieldAlias, cellFormat, gridType, currencyCode),
    [cellOptions, baselineValue, dbType, fieldAlias, cellFormat, gridType, currencyCode]
  );
  const aiDisplay = useMemo(
    () =>
      matchAiOption(cellOptions, aiValue)?.label ??
      formatDisplayValue(aiValue, dbType, fieldAlias, cellFormat, gridType, currencyCode),
    [cellOptions, aiValue, dbType, fieldAlias, cellFormat, gridType, currencyCode]
  );
  const hasCurrentValue = baselineValue !== null && baselineValue !== undefined && baselineValue !== '';
  const valuesDiffer = aiValue !== undefined && hasCurrentValue && currentDisplay !== aiDisplay;
  // SCAL-8976: the "current -> ai" purple merge text is hover/active-only now. It
  // shows only while this exact cell is the review card being hovered or the active
  // (selected) card. At rest the cell renders its normal value and stays editable.
  // The selector is per-cell, so only this viewer re-renders when its match flips.
  const showMerge = useCellShowsMerge(securityId, fieldAlias);
  const renderMerge = valuesDiffer && showMerge;
  // SCAL-9150: drawer-open IS the review context. A still-pending cell flags itself
  // AI-purple BY DEFAULT while the drawer is open (no card hovered/selected needed),
  // then reverts to normal colors when the drawer closes. This only recolors the
  // resting AI preview - the previous value is still revealed solely via the hover/
  // active merge text below, never at rest.
  const drawerOpen = useReviewDrawerOpen();
  const restIsPurple = isPending && drawerOpen && !renderMerge;
  const currentDisplayOrNA = currentDisplay || 'N/A';
  // Full, untruncated "current -> ai" text. Available whenever the values differ - not
  // just in the hovered/active merge state - so the same string powers both the merge
  // tooltip AND the rest-state reveal tooltip below (renderMerge implies valuesDiffer).
  const mergeTooltipText = valuesDiffer ? `${currentDisplayOrNA} → ${aiDisplay}` : '';

  // The raw AI value fed through the clone-preview rendered BLANK for select
  // cells (the AI suggests the option's NAME) - and with no text the purple
  // gradient (background-clip: text) was invisible. Resolve the suggestion
  // through the shared resolver and preview the resolved ID through the cell's
  // own viewer; when nothing resolves, preview the FORMATTED text instead so
  // the pending flag always has text to paint.
  const resolvedAiValue = useMemo(() => {
    if (!cellOptions?.length) {
      // Numeric coercion (SCAL-9263): a raw doc token like "$133.5" fails
      // FastValueViewer's isNaN gate and would render verbatim with the
      // document's precision instead of the cell's own (2dp currency) format.
      return finiteNumberOf(aiValue) ?? aiValue;
    }
    return resolveAiOptionValue({ options: cellOptions }, aiValue)?.id;
  }, [cellOptions, aiValue]);
  const previewAsText = isPending && Boolean(cellOptions?.length) && resolvedAiValue === undefined;

  // SCAL-9335: non-reviewers get the plain cell with its REAL value - no AI
  // preview (a pending cell previews the AI value below), no merge text, no
  // popover, no purple.
  const reviewerMode = useReviewerMode();
  if (!reviewerMode) {
    return (
      <FallbackValueViewer
        value={value}
        originalValueViewer={OriginalValueViewer}
        restProps={restProps}
        onDoubleClick={wrappedOnDoubleClick}
      />
    );
  }

  // At rest, render through the cell's OWN value viewer so formatting, alignment and
  // padding stay single-sourced with every other cell (no bespoke layout here). A
  // pending field previews the AI value - fed in via a cell clone, since the viewers
  // read cell.value, not the value prop - while a settled field shows its real value.
  // Normal color either way; the AI gradient is reserved for the hovered merge view.
  const restContent = previewAsText ? (
    <FastValueViewer
      value={aiDisplay}
      {...restProps}
      cell={{ ...restProps.cell, gridType: undefined, value: aiDisplay }}
      onDoubleClick={wrappedOnDoubleClick}
    />
  ) : (
    <FallbackValueViewer
      value={(isPending ? resolvedAiValue : value) as string | number}
      originalValueViewer={OriginalValueViewer}
      restProps={
        isPending && restProps.cell ? { ...restProps, cell: { ...restProps.cell, value: resolvedAiValue } } : restProps
      }
      onDoubleClick={wrappedOnDoubleClick}
    />
  );

  // Even at rest (when the transient merge text is NOT shown), keep the full previous
  // value reachable: while the suggestion is PENDING and the values differ, wrap the
  // cell's own viewer in a reveal Tooltip carrying the untruncated "current -> ai".
  // The viewer may not forward a ref or accept mouse events, so anchor the Tooltip on
  // a ref-forwarding span that mirrors the cell's layout (restRevealAnchorSx) - no
  // change to alignment, width or padding. While the drawer is open a pending cell
  // uses the AI-purple variant of that anchor (restPurpleAnchorSx) so its resting AI
  // preview reads purple (SCAL-9150). A SETTLED cell renders bare and plain: accepted
  // shows its (now applied) value, dismissed shows the untouched value, and neither
  // advertises the AI suggestion at rest anymore.
  const restReveal = valuesDiffer && isPending;
  const restAnchored =
    restReveal || restIsPurple ? (
      <Box
        component="span"
        sx={restIsPurple ? restPurpleAnchorSx : restRevealAnchorSx}
        data-review-purple={restIsPurple || undefined}>
        {restContent}
      </Box>
    ) : (
      restContent
    );
  const restWithReveal = restReveal ? (
    <Tooltip title={mergeTooltipText} placement="top" arrow>
      {restAnchored}
    </Tooltip>
  ) : (
    restAnchored
  );

  return (
    <AISecurityFindingPopover
      fieldMetadata={aiFieldMetadata}
      accuracyConfidence={aiAccuracyConfidence}
      fieldLabel={fieldLabel}
      status={status}
      onAccept={canDecide && securityId && fieldAlias ? handleAccept : undefined}
      // eslint-disable-next-line no-nested-ternary
      onRemove={canDecide ? (securityId && fieldAlias ? handleDismiss : onRemoveFinding) : undefined}
      pendingCitation={pendingCitation}
      onAcceptCitation={pendingCitation ? handleAcceptCitation : undefined}
      pendingCitationDocumentName={pendingCitationDocumentName}>
      {({ onClick }) => (
        <Box
          // Tells the grid that ctrl/cmd-click here is the popover's, not a multi-select.
          data-selection-passthrough="modifier"
          onMouseDownCapture={e => {
            // Intercept (and open the in-cell finding popover via onClickCapture)
            // only while the purple merge text is actually shown, or on ctrl/meta.
            // At rest the cell is plain and editable, so let the event fall through.
            if (renderMerge || isFindingPopoverClick(e)) {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation?.();
            }
          }}
          onClickCapture={e => {
            if (renderMerge || isFindingPopoverClick(e)) {
              onClick(e);
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation?.();
            }
          }}
          sx={getMergeBoxSx(renderMerge, ctrlHeld)}>
          {renderMerge ? (
            <Tooltip title={mergeTooltipText} placement="top" arrow>
              <Typography component="span" variant="body2" noWrap sx={mergeTypographySx}>
                {/* The "before" value stays neutral; only the AI value carries the
                    gradient so it is obvious which side is the AI suggestion. */}
                <Box component="span" sx={mergeCurrentValueSx}>
                  {currentDisplayOrNA}
                </Box>
                <Box component="span" sx={mergeArrowSx}>
                  →
                </Box>
                <Box component="span" sx={{ ...mergeAIValueSx, ...(aiGradientTextSx as object) }}>
                  {aiDisplay}
                </Box>
              </Typography>
            </Tooltip>
          ) : (
            restWithReveal
          )}
        </Box>
      )}
    </AISecurityFindingPopover>
  );
};

export default AIMergeValueViewer;
